// The World owns all chunks and exposes block get/set in world coordinates.
// It also holds entities (player, mobs, dropped items) and world time.

import { Block, isSolid } from './blocks';
import { Chunk } from './chunk';
import { SEA_LEVEL, WORLD_HEIGHT } from './constants';
import { chunkKey, localX, localZ, worldToChunkX, worldToChunkZ } from './math';
import { WorldGen } from './worldgen';

export class World {
  readonly gen: WorldGen;
  readonly chunks = new Map<string, Chunk>();
  /** World time in seconds; drives the day/night cycle. */
  time = 0;

  constructor(seed: number) {
    this.gen = new WorldGen(seed);
  }

  get seed(): number {
    return this.gen.seed;
  }

  hasChunk(cx: number, cz: number): boolean {
    return this.chunks.has(chunkKey(cx, cz));
  }

  getChunk(cx: number, cz: number): Chunk | undefined {
    return this.chunks.get(chunkKey(cx, cz));
  }

  /** Get (generating if needed) the chunk at chunk coords. */
  ensureChunk(cx: number, cz: number): Chunk {
    const key = chunkKey(cx, cz);
    let chunk = this.chunks.get(key);
    if (!chunk) {
      chunk = new Chunk(cx, cz);
      this.gen.generateChunk(chunk);
      this.chunks.set(key, chunk);
    }
    return chunk;
  }

  removeChunk(cx: number, cz: number): void {
    this.chunks.delete(chunkKey(cx, cz));
  }

  /** Overlay a saved (player-edited) chunk's blocks onto generated terrain. */
  applySavedChunk(cx: number, cz: number, blocks: Uint8Array): void {
    const chunk = this.ensureChunk(cx, cz);
    chunk.blocks.set(blocks);
    chunk.dirty = true;
  }

  /** Chunks the player has modified (for saving). */
  modifiedChunks(): Chunk[] {
    const out: Chunk[] = [];
    for (const chunk of this.chunks.values()) {
      if (chunk.dirty) out.push(chunk);
    }
    return out;
  }

  getBlock(x: number, y: number, z: number): Block {
    if (y < 0 || y >= WORLD_HEIGHT) return Block.Air;
    const chunk = this.getChunk(worldToChunkX(x), worldToChunkZ(z));
    if (!chunk) return Block.Air;
    return chunk.get(localX(x), y, localZ(z));
  }

  /** Set a block, marking the chunk dirty. Returns true if it changed. */
  setBlock(x: number, y: number, z: number, block: Block): boolean {
    if (y < 0 || y >= WORLD_HEIGHT) return false;
    const chunk = this.getChunk(worldToChunkX(x), worldToChunkZ(z));
    if (!chunk) return false;
    const lx = localX(x);
    const lz = localZ(z);
    if (chunk.get(lx, y, lz) === block) return false;
    chunk.set(lx, y, lz, block);
    chunk.dirty = true;
    return true;
  }

  isSolidAt(x: number, y: number, z: number): boolean {
    return isSolid(this.getBlock(Math.floor(x), Math.floor(y), Math.floor(z)));
  }

  /** Highest solid (or water surface) block Y in a column; -1 if empty. */
  columnTop(x: number, z: number): number {
    for (let y = WORLD_HEIGHT - 1; y >= 0; y--) {
      const b = this.getBlock(x, y, z);
      if (b !== Block.Air && b !== Block.Water) return y;
    }
    return -1;
  }

  /** A safe spawn Y (standing position) for a column. */
  surfaceSpawnY(x: number, z: number): number {
    const top = this.columnTop(x, z);
    return Math.max(SEA_LEVEL, top) + 1;
  }
}
